import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { PortfolioConnect } from '@cas-parser/connect';

import { ThemeContext } from '../../../context/ThemeContext';
import LandingLayout from '../LandingLayout';
import { usePortfolioUser } from './usePortfolioUser';
import ImportedCasPortfolio from './ImportedCasPortfolio';
import JsonCasImportPanel from './JsonCasImportPanel';
import './ImportInvestmentsPage.css';

const METHODS = [
  {
    id: 'smart',
    title: 'Smart import',
    subtitle: 'PDF, Gmail, CDSL OTP, and more',
    detail: 'One assistant for PDF upload, Gmail/Outlook inbox, CDSL OTP, KFintech email request, or inbound email forwarding.',
    icon: <AppstoreOutlined />,
    steps: ['Open Smart import', 'Pick a source', 'Review holdings'],
  },
  {
    id: 'json',
    title: 'JSON file',
    subtitle: 'Import already-parsed CAS JSON',
    detail: 'Upload a CASParser-style JSON export (like imported-portfolio.json) to test holdings without a PDF.',
    icon: <FileTextOutlined />,
    steps: ['Choose JSON file', 'Validate shape', 'Review holdings'],
  },
];

function ImportInvestmentsPage() {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId, isAuthenticated, loading: userLoading } = usePortfolioUser();
  const openPortfolioConnectRef = useRef(null);
  const importedCasRef = useRef(null);
  const workspaceRef = useRef(null);

  const initialMethod = useMemo(() => {
    const fromQuery = searchParams.get('method');
    if (METHODS.some((m) => m.id === fromQuery)) return fromQuery;
    return 'smart';
  }, [searchParams]);

  const [method, setMethod] = useState(initialMethod);
  const [importedCas, setImportedCas] = useState(null);

  useEffect(() => {
    setMethod(initialMethod);
  }, [initialMethod]);

  const active = METHODS.find((m) => m.id === method) || METHODS[0];

  const handleCasSuccess = useCallback((data, metadata) => {
    setImportedCas({ data, metadata, importedAt: Date.now() });
    requestAnimationFrame(() => {
      importedCasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const selectMethod = (id) => {
    setMethod(id);
    requestAnimationFrame(() => {
      workspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  return (
    <LandingLayout>
      <div className={`ii-page${isDark ? ' ii-page--dark' : ''}`}>
        <div className="ii-atmosphere" aria-hidden="true" />

        <header className="ii-hero">
          <p className="ii-brand">SimplyBond</p>
          <h1 className="ii-title">Import your investments</h1>
          <p className="ii-lede">
            Bring holdings from a CAS statement into SimplyBond — use Smart import, or upload a parsed JSON file.
          </p>
        </header>

        {userLoading && (
          <div className="ii-loading">
            <Spin size="large" />
            <p>Loading…</p>
          </div>
        )}

        {!userLoading && !isAuthenticated && (
          <div className="ii-login-gate">
            <p className="ii-login-title">Sign in to import</p>
            <p className="ii-login-hint">
              Your SimplyBond account keeps imports private and lets you review holdings before saving.
            </p>
            <button
              type="button"
              className="ii-btn ii-btn--primary"
              onClick={() => navigate('/login', { state: { from: '/import-investments' } })}
            >
              Log in <ArrowRightOutlined />
            </button>
          </div>
        )}

        {!userLoading && isAuthenticated && (
          <>
            <section className="ii-methods" aria-label="Import methods">
              {METHODS.map((item) => {
                const selected = item.id === method;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`ii-method${selected ? ' ii-method--active' : ''}`}
                    onClick={() => selectMethod(item.id)}
                    aria-pressed={selected}
                  >
                    <span className="ii-method-icon">{item.icon}</span>
                    <span className="ii-method-copy">
                      <span className="ii-method-title">{item.title}</span>
                      <span className="ii-method-sub">{item.subtitle}</span>
                    </span>
                    {selected && <CheckCircleOutlined className="ii-method-check" />}
                  </button>
                );
              })}
            </section>

            <section className="ii-workspace" ref={workspaceRef}>
              <div className="ii-workspace-intro">
                <h2 className="ii-workspace-title">{active.title}</h2>
                <p className="ii-workspace-detail">{active.detail}</p>
                <ol className="ii-steps">
                  {active.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>

              <div className="ii-workspace-body">
                {method === 'smart' && (
                  <div className="ii-guided">
                    <p className="ii-guided-copy">
                      Opens Smart import for PDF upload, Gmail/Outlook inbox, inbound email forwarding, KFintech request, or CDSL OTP — then returns parsed holdings here.
                    </p>
                    <button
                      type="button"
                      className="ii-btn ii-btn--primary"
                      onClick={() => openPortfolioConnectRef.current?.()}
                    >
                      Start Smart import <ArrowRightOutlined />
                    </button>
                  </div>
                )}
                {method === 'json' && (
                  <JsonCasImportPanel
                    isDark={isDark}
                    onCasImported={handleCasSuccess}
                    embedded
                  />
                )}
              </div>
            </section>
          </>
        )}

        {importedCas && (
          <div ref={importedCasRef} className="ii-result">
            <ImportedCasPortfolio
              key={importedCas.importedAt}
              data={importedCas.data}
              metadata={importedCas.metadata}
              isDark={isDark}
              userId={userId}
              onClear={() => setImportedCas(null)}
            />
          </div>
        )}
      </div>

      {createPortal(
        <PortfolioConnect
          accessToken="sk_a8b8b612c55301b3dd03cce95182cf61"
          config={{
            homeLayout: 'asset-type',
            enableGenerator: true,
            enableCdslFetch: true,
            enableInbox: true,
            enableInboundEmail: true,
            inboundEmail: { pollIntervalMs: 3000 },
            successBehavior: 'summary',
            successCta: {
              label: 'View imported portfolio',
              onClick: () => {
                importedCasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              },
            },
            inbox: {
              redirectUri: 'https://connect.casparser.in/oauth/callback',
            },
            theme: { mode: isDark ? 'dark' : 'light' },
          }}
          onSuccess={handleCasSuccess}
          onError={(error) => console.error(error)}
        >
          {({ open }) => {
            openPortfolioConnectRef.current = open;
            return null;
          }}
        </PortfolioConnect>,
        document.body,
      )}
    </LandingLayout>
  );
}

export default ImportInvestmentsPage;
